import React, { useContext, useState, useEffect } from 'react';
import { useUpdatePracticalEvidenceMutation } from '../../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { UPDATE_PRACTICAL_EVIDENCE } from '../../../Reducers/Actions';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import EditIcon from '@mui/icons-material/Edit';
import { MenuItem, ListItemIcon, ListItemText, Grid } from '@mui/material';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import FileUpload from '../../../SystemDesign/fileUpload';

/**
 * UpdatePracticleEvidence
 * Renders a menu action that opens a dialog to update a practical evidence.
 * Mirrors the structure and UX of UpdatePoste.jsx.
 */
const UpdatePracticleEvidence = ({ row, label, noTrigger = false, forceOpen = false, onClose, closeParentMenu }) => {
  const [updateEvidence] = useUpdatePracticalEvidenceMutation();
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [dialogOpen, setDialogOpen] = useState(Boolean(forceOpen));
  useEffect(() => {
    setDialogOpen(Boolean(forceOpen));
  }, [forceOpen]);

  // Safely access evidence from row
  const evidence = row?.original || {};
  const evidenceId = evidence?.evidence_id || evidence?.id;

  // Form state
  const initialValues = {
    evidence_url: evidence?.evidence_url || evidence?.url || '',
  };
  const [formValues, setFormValues] = useState(initialValues);
  // File upload state: seed with existing file for display
  const existingFileName = evidence?.file_name || (evidence?.evidence_url ? evidence.evidence_url.split('/').pop() : '');
  const inferMimeFromName = (name) => {
    const lower = String(name || '').toLowerCase();
    if (/(\.png|\.jpg|\.jpeg|\.gif|\.webp)$/.test(lower)) return 'image/*';
    if (/(\.mp4|\.mov|\.avi|\.webm)$/.test(lower)) return 'video/*';
    if (/(\.pdf|\.docx?|\.pptx?|\.xlsx?)$/.test(lower)) return 'application/pdf';
    if (/(\.zip|\.rar|\.7z)$/.test(lower)) return 'application/zip';
    return 'application/octet-stream';
  };
  const existingFileType = evidence?.evidence_type || evidence?.evidenceType || '';
  const [selectedFiles, setSelectedFiles] = useState([]);

  // When dialog opens, refresh selectedFiles from latest evidence so inference uses up-to-date values
  useEffect(() => {
    if (dialogOpen && selectedFiles.length === 0) {
      // only seed once for display context; do not overwrite after user chooses
      const latestName = evidence?.file_name || (evidence?.evidence_url ? evidence.evidence_url.split('/').pop() : '');
      if (latestName) {
        const latestType = evidence?.evidence_type || evidence?.evidenceType || '';
        const seededType = latestType
          ? (latestType === 'document' ? inferMimeFromName(latestName) : (latestType === 'pdf' ? 'application/pdf' : `${latestType}/*`))
          : inferMimeFromName(latestName);
        setSelectedFiles([{ name: latestName, type: seededType }]);
      }
    }
    if (!dialogOpen) setSelectedFiles([]);
  }, [dialogOpen, evidence, selectedFiles.length]);

  const formValidationSchema = yup.object().shape({});

  const handleUpdate = async (values) => {
    try {
      if (!evidenceId) throw new Error('No evidence ID found');

      // -------------------------------
      // 1. Infer evidence_type
      // -------------------------------
      let inferredType = evidence?.evidence_type || evidence?.evidenceType;

      if (selectedFiles && selectedFiles.length > 0) {
        const f = selectedFiles[0];
        const t = (f?.type || '').toLowerCase();
        const n = (f?.name || '').toLowerCase();

        if (t.startsWith('image') || /\.(png|jpg|jpeg|gif|webp)$/.test(n)) inferredType = 'image';
        else if (t.startsWith('video') || /\.(mp4|mov|avi|webm)$/.test(n)) inferredType = 'video';
        else if (t === 'application/pdf' || /\.(pdf|docx?|pptx?|xlsx?)$/.test(n)) inferredType = 'document';
        else if (/\.(zip|rar|7z)$/.test(n)) inferredType = 'document';

        // Also set evidence_url to chosen file name (for display)
        values.evidence_url = f?.name || values.evidence_url;
      }

      if (!selectedFiles?.length && values.evidence_url) {
        const nn = values.evidence_url.toLowerCase();
        if (/\.(png|jpg|jpeg|gif|webp)$/.test(nn)) inferredType = 'image';
        else if (/\.(mp4|mov|avi|webm)$/.test(nn)) inferredType = 'video';
        else if (/\.(pdf|docx?|pptx?|xlsx?)$/.test(nn)) inferredType = 'document';
        else if (/\.(zip|rar|7z)$/.test(nn)) inferredType = 'document';
      }

      // -------------------------------
      // 2. Build FormData for API
      // -------------------------------
      const formData = new FormData();

      // If a new file chosen, append it
      if (selectedFiles && selectedFiles.length > 0 && selectedFiles[0] instanceof File) {
        formData.append('file', selectedFiles[0]);
      }

      // Metadata fields
      if (inferredType) formData.append('evidence_type', inferredType.trim());
      if (values.evidence_url) formData.append('evidence_url', values.evidence_url.trim());
      if (values.metadata) formData.append('metadata', JSON.stringify(values.metadata));
      if (values.captured_at) formData.append('captured_at', values.captured_at);
      if (values.location) formData.append('location', values.location);

      // -------------------------------
      // 3. Call API
      // -------------------------------
      const response = await updateEvidence({
        evidenceId: String(evidenceId),
        body: formData, // must be FormData for file upload
      }).unwrap();

      // -------------------------------
      // 3.1 Handle antivirus scan results (if provided by backend)
      // -------------------------------
      const scanResults = Array.isArray(response?.scanResults) ? response.scanResults : [];
      const infectedItems = scanResults.filter((sr) => sr?.isInfected);
      if (infectedItems.length > 0) {
        const msg = infectedItems
          .map((sr) => {
            const fname = sr?.file || selectedFiles?.[0]?.name || evidence?.file_name || 'file';
            const viruses = Array.isArray(sr?.viruses) && sr.viruses.length ? sr.viruses.join(', ') : 'virus detected';
            return `${fname}: ${viruses}`;
          })
          .join(' | ');
        setInfoAlert({ severity: 'error', message: `Security scan failed - ${msg}` });
        setOpenSnackBar(true);
      }

      // -------------------------------
      // 4. Update Redux store optimistically
      // -------------------------------
      const pickedName =
          selectedFiles?.[0]?.name ||
          (values.evidence_url ? values.evidence_url.split('/').pop() : undefined);

      const updatedForStore = {
        ...evidence,
        id: evidenceId,
        // keep evaluation reference so list filtering includes this item
        evaluation_id: evidence?.evaluation_id || evidence?.evaluationId,
        evaluationId: evidence?.evaluationId || evidence?.evaluation_id,
        evidence_type: inferredType?.trim() || evidence?.evidence_type,
        evidenceType: inferredType?.trim() || evidence?.evidenceType || evidence?.evidence_type,
        evidence_url: values.evidence_url?.trim() || evidence?.evidence_url,
        url: values.evidence_url?.trim() || evidence?.evidence_url || evidence?.url,
        file_name: pickedName || evidence?.file_name,
        original_name: pickedName || evidence?.original_name,
        client_name: pickedName || evidence?.client_name,
        updatedAt: new Date().toISOString(),
        // attach scan results so UI can reflect infection state
        _scanResults: scanResults,
      };

      dispatch({ type: UPDATE_PRACTICAL_EVIDENCE, payload: updatedForStore });

      // -------------------------------
      // 5. UI feedback + cleanup
      // -------------------------------
      setInfoAlert({ severity: 'success', message: 'Evidence updated successfully' });
      setOpenSnackBar(true);
      onClose?.();
      try { typeof closeParentMenu === 'function' && closeParentMenu(); } catch (_) {}
      setDialogOpen(false);
      setSelectedFiles([]);
      return true;
    } catch (error) {
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || 'An error occurred while updating evidence',
      });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = noTrigger ? null : (
    <MenuItem onClick={() => setDialogOpen(true)}>
      <ListItemIcon>
        <EditIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{label || 'Edit Evidence'}</ListItemText>
    </MenuItem>
  );

  const dialogContent = (
    <Formik
      initialValues={formValues}
      validationSchema={formValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
      onSubmit={handleUpdate}
    >
      {(formik) => (
        <Form id="updatePracticalEvidenceForm">
          <Grid container spacing={2} marginTop={2}>
            <Grid item size={{ xs: 12 }}>
              <FileUpload
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                onFileSelect={(fileItem) => {
                  const fname = fileItem?.name || '';
                  formik.setFieldValue('evidence_url', fname || evidence?.evidence_url || '');
                  setFormValues((s) => ({ ...s, evidence_url: fname || s.evidence_url }));
                }}
                label="Replace file"
                description="images, videos, or any file"
                acceptedFormats="or browse files on your computer"
                browseButtonLabel="Choose file"
                removeButtonLabel="Remove"
                uploadedFileLabel="Current file"
                uploadedFileTooltip={existingFileName}
                multiple={false}
              />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  return (
    <>
      {triggerButton}
      <MuiDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); onClose && onClose(); try { typeof closeParentMenu === 'function' && closeParentMenu(); } catch (_) {} }}
        icon={<EditIcon />}
        title="Update Practical Evidence"
        subtitle={`Update evidence #${evidenceId || ''}.`}
        content={dialogContent}
        existAction={true}
        actionTitle="Update Evidence"
        actionType="submit"
        formId="updatePracticalEvidenceForm"
      />
    </>
  );
};

UpdatePracticleEvidence.menuIcon = <EditIcon fontSize="small" />;
UpdatePracticleEvidence.menuLabel = 'Edit Evidence';

export default UpdatePracticleEvidence;

